'use client';

import { useMemo, useState } from 'react';
import { buildWhatsappLink } from '@/lib/browser-widget-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserWhatsappButtonGenerator() {
  const [phone, setPhone] = useState('1 555 010 2030');
  const [message, setMessage] = useState('Hi! I have a question about your service.');
  const [buttonLabel, setButtonLabel] = useState('Chat on WhatsApp');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const result = useMemo(
    () => buildWhatsappLink(phone, message, buttonLabel),
    [phone, message, buttonLabel],
  );

  async function handleCopy(value: string, label: string) {
    try {
      await copyText(value);
      setCopyMessage(`${label} copied to clipboard.`);
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">WhatsApp</span>
        <h2 className="tool-live-title">WhatsApp button generator</h2>
        <p className="tool-live-subtitle">
          Create a click-to-chat wa.me link with a prefilled message, plus a ready-to-paste
          &ldquo;Chat on WhatsApp&rdquo; button for your website.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Phone number (with country code)</span>
              <input
                inputMode="tel"
                onChange={(e) => setPhone(e.target.value)}
                placeholder="1 555 010 2030"
                type="text"
                value={phone}
              />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Prefilled message</span>
              <textarea onChange={(e) => setMessage(e.target.value)} rows={3} value={message} />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Button label</span>
              <input
                onChange={(e) => setButtonLabel(e.target.value)}
                type="text"
                value={buttonLabel}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={!!result.error}
              onClick={() => handleCopy(result.link, 'wa.me link')}
              type="button"
            >
              Copy wa.me link
            </button>
            <button
              className="button button-secondary"
              disabled={!!result.error}
              onClick={() => handleCopy(result.buttonHtml, 'Button HTML')}
              type="button"
            >
              Copy button HTML
            </button>
          </div>
          {result.error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {result.error}
            </p>
          ) : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <span
            style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}
          >
            Button preview
          </span>
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'center',
              overflowX: 'auto',
            }}
            dangerouslySetInnerHTML={{ __html: result.buttonHtml }}
          />

          <label className="tool-live-field tool-live-field-full" style={{ marginTop: '16px' }}>
            <span>wa.me link</span>
            <input readOnly value={result.link} />
          </label>
          <label className="tool-live-field tool-live-field-full">
            <span>api.whatsapp.com link</span>
            <input readOnly value={result.apiLink} />
          </label>
          <label className="tool-live-field tool-live-field-full">
            <span>Button HTML snippet</span>
            <textarea readOnly rows={8} value={result.buttonHtml} />
          </label>
          <p className="tool-live-panel-note">
            Generated locally in your browser. Nothing is uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
